/**
 * markdownSafety
 *
 * Shared allow-list helpers for rendering model-generated Markdown safely.
 *
 * A model reply is untrusted content. Two concrete risks when it is rendered
 * as Markdown:
 *   - an embedded image (`![alt](https://evil.example/x.png)`) causes the
 *     browser to issue a request to an attacker-controlled host the moment the
 *     reply renders (a tracking / exfiltration beacon), before the user does
 *     anything;
 *   - a link (`[click](https://evil.example)`) rendered as a live anchor lets a
 *     single click navigate the user off to an attacker page.
 *
 * These helpers decide, by host, whether a URL may be loaded as an image or
 * presented as a live link. Everything else is rendered inert (alt text for an
 * image, plain text for a link).
 */

/**
 * Host suffixes considered Amazon-internal and therefore allow-listed.
 *
 * A URL is allowed when its host equals one of these exactly or ends with
 * `"." + suffix` (so `foo.amazon.com` matches `amazon.com`, but
 * `amazon.com.evil.com` and `notamazon.com` do NOT).
 */
export const ALLOWED_HOST_SUFFIXES: readonly string[] = [
  'amazon.com',
  'amazon.dev',
  'aws.dev',
  'a2z.com',
];

/**
 * True when `host` is exactly an allow-listed suffix or a sub-domain of one.
 * Matching is case-insensitive and never substring-based.
 */
function isAllowedHost(host: string): boolean {
  const h = host.toLowerCase();
  return ALLOWED_HOST_SUFFIXES.some(
    (suffix) => h === suffix || h.endsWith(`.${suffix}`),
  );
}

/**
 * Resolve a possibly-relative href against the current origin and return it
 * only when it is safe to treat as a live link / loadable image:
 *   - a same-origin relative path (`/docs`, `./x`) is allowed and returned
 *     resolved;
 *   - an absolute `http:`/`https:` URL is allowed only when its host is
 *     allow-listed; `https` is required for absolute URLs so a downgrade to
 *     `http` cannot smuggle a host past the check;
 *   - anything else (other schemes such as `javascript:`, `data:`, `mailto:`,
 *     or an unparseable value) returns `null`.
 *
 * Returns the resolved absolute URL string when allowed, otherwise `null`.
 */
export function resolveAllowedUrl(href: string | undefined | null): string | null {
  if (!href) return null;
  const raw = href.trim();
  if (!raw) return null;

  // Base used both to resolve relative URLs and to recognise same-origin.
  const base =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';

  let url: URL;
  try {
    url = new URL(raw, base);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    // javascript:, data:, mailto:, tel:, blob:, ... — never a loadable image
    // or a navigable allow-listed link.
    return null;
  }

  // Same-origin (including relative paths resolved against the origin) is safe.
  if (url.origin === base) return url.href;

  // Cross-origin absolute URLs must be https and on an allow-listed host.
  if (url.protocol !== 'https:') return null;
  if (!isAllowedHost(url.hostname)) return null;

  return url.href;
}

/** True when the href may be loaded as an image / presented as a live link. */
export function isAllowedUrl(href: string | undefined | null): boolean {
  return resolveAllowedUrl(href) !== null;
}
